import type { CreasePattern, SelectionRef } from '@/origami/types';
import type { Vec2 } from '@/geometry/vec2';
import { dist2 } from '@/geometry/vec2';
import { closestPointOnSegment } from '@/geometry/intersect';
import { pointInPolygon } from '@/geometry/polygon';
import { PICK_RADIUS_PX } from '@/constants/numeric';
import { pxToWorld, type Viewport } from './viewport';

/**
 * Pick the topmost item under a world point. Vertices win over edges, edges over
 * faces - the usual CAD precedence, so a click near a junction selects the
 * junction rather than one of the lines through it.
 */
export function hitTest(
  pattern: CreasePattern,
  world: Vec2,
  vp: Viewport,
  filter: { vertices?: boolean; edges?: boolean; faces?: boolean } = {},
): SelectionRef | null {
  const radius = pxToWorld(vp, PICK_RADIUS_PX);
  const coords = pattern.verticesCoords;

  if (filter.vertices !== false) {
    let best = -1;
    let bestD = radius;
    for (let v = 0; v < coords.length; v++) {
      const d = dist2(coords[v], world);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    if (best >= 0) return { kind: 'vertex', index: best };
  }

  if (filter.edges !== false) {
    let best = -1;
    let bestD = radius;
    for (let e = 0; e < pattern.edgesVertices.length; e++) {
      const [a, b] = pattern.edgesVertices[e];
      const r = closestPointOnSegment(world, coords[a], coords[b]);
      if (r.distance < bestD) {
        bestD = r.distance;
        best = e;
      }
    }
    if (best >= 0) return { kind: 'edge', index: best };
  }

  if (filter.faces !== false) {
    for (let f = 0; f < pattern.facesVertices.length; f++) {
      const loop = pattern.facesVertices[f].map((i) => coords[i]);
      if (pointInPolygon(world, loop)) return { kind: 'face', index: f };
    }
  }

  return null;
}

export function refEquals(a: SelectionRef | null, b: SelectionRef | null): boolean {
  if (!a || !b) return a === b;
  return a.kind === b.kind && a.index === b.index;
}
